import { DOMHelpers } from 'helpers/dom';
import { QuillBinding, QuillMatcher } from 'interfaces/quill';
import { Observable, SubscribableOrNullableValue, SubscribableOrValue } from 'knockout';
import { Quill, SelectionChangeHandler } from 'libraries/quill';
import { withEffects } from 'mixins/withEffects';
import QuillImageDropAndPaste from 'quill-image-drop-and-paste';
import { withEffect } from '../../../../mixins/withEffect';
import { ChatEditorEmojiBlot } from '../blots/emoji';
import { IChatEditorBlot, IChatEditorBlotInstance } from '../blots/iblot';

Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste);

const defaultFormats: Array<QuillFormats> = [
    'bold',
    'italic',
    'underline',
    'color',
    'wcc-emoji'
]

export type QuillFormats = 'bold' | 'italic' | 'underline' | 'color' | 'wcc-emoji'

interface QuillWrapperConfig {
    node: SubscribableOrValue<HTMLElement | undefined>
    placeholder?: SubscribableOrNullableValue<string>
    bindings?: StringMap<QuillBinding>
    blots?: Array<IChatEditorBlot>
    formats?: Array<QuillFormats>
    onSelectionChange?: SelectionChangeHandler
    onTextChange?: (editor: Quill) => void
}

export class QuillWrapper {
    private blots: Array<IChatEditorBlot>
    quill: Observable<Quill | undefined> = ko.observable()

    isReady = ko.pureComputed(() => this.quill() != undefined)

    dispose: Action

    constructor(config: QuillWrapperConfig) {
        const {
            node,
            onSelectionChange = _.noop,
            onTextChange = _.noop
        } = config;

        const allowedFormats = config.formats ?? defaultFormats;

        this.blots = config.blots ?? [];

        if (allowedFormats.includes('wcc-emoji'))
            this.blots.push(<any><IChatEditorBlot<string, HTMLImageElement>>ChatEditorEmojiBlot);

        const bindings = this.blots.reduce((bindings, blot) => ({ ...blot.bindings, ...bindings }), config.bindings);
        const matchers = this.blots.reduce((matchers, blot) => blot.matchers != undefined ? [...matchers, ...blot.matchers] : matchers, new Array<QuillMatcher>());
        const blotsFormats = this.blots.map(blot => blot.blotName);        

        const placeholder = ko.flattenComputed(config.placeholder, '');
        const formats: Array<string> = _.uniq([...allowedFormats, ...blotsFormats]);

        this.blots.forEach(blot => Quill.register(`blots/${blot.blotName}`, blot, true));

        const effects = withEffects();

        effects.register(node => {
            if (node) {
                const quill = new Quill(node, {
                    formats: formats,

                    modules: {
                        keyboard: { bindings: bindings },
                        clipboard: { matchers: matchers },
                        imageDropAndPaste: { handler: _.noop }
                    }                    
                });

                quill.on('selection-change', onSelectionChange);
                quill.on('text-change', () => onTextChange(quill as Quill));

                this.quill(quill);

                return [
                    withEffect(placeholder => {
                        quill.root.dataset.placeholder = placeholder;
                    }, [placeholder]),

                    () => node.innerHTML = ''
                ]
            }            

            this.quill(undefined);
        }, [node]);

        this.dispose = effects.dispose;
    }    

    getContent = () => {
        return this.quill.invokeNotNull(quill => {
            const html = Array.from(quill.root.children)
                .filter(node => node.tagName.toLowerCase() === 'p')
                .reduce((result, node) => result + this.getNodeContent(node) + '\n', '');

            return html.trim();
        }) ?? '';
    }    

    private getNodeContent(node: Node): string {
        if (DOMHelpers.isHTMLElement(node)) {
            if (node.tagName.toLowerCase() === 'p')
                return Array.from(node.childNodes).reduce((result, node) => result + this.getNodeContent(node), '');

            if (node.tagName.toLowerCase() === 'br')
                return '';  // this will be replaced by '\n' in the parent node

            const blotContent = this.blots.reduce((content, blot) => {
                if (content == undefined && this.isBlotMatch(node, blot)) {
                    const instance = <IChatEditorBlotInstance>Quill.find(node);

                    if (instance != undefined)
                        return instance.toString();
                }

                return content;
            }, <string | undefined>undefined);

            if (blotContent != undefined)
                return blotContent;

            return node.outerHTML;
        } else {
            switch (node.nodeType) {
                case Node.TEXT_NODE: return node.nodeValue ?? '';
                default: return '';
            }
        }        
    }

    private isBlotMatch(node: Node, blot: IChatEditorBlot) {
        if (DOMHelpers.isHTMLElement(node)) {
            const tagName = blot.tagName.toLowerCase();
            const className = blot.className?.toLowerCase();

            return tagName == node.tagName.toLowerCase() &&
                (className == undefined || node.classList.contains(className));
        }

        return false;
    }
}